import * as _ from "lodash";
import { CanvasMode } from "../../widgets/interfaces/model/enumerations/CanvasMode";
import { ICanvasDimension, ICanvasEditorState, INonUndoableAction } from "../../widgets/interfaces/model/index";

/**
 * Set canvas container
 */
export interface ISetCanvasContainerAction extends INonUndoableAction {
    canvasContainerEl: HTMLElement;
};

/**
 * Set Canvas editor dimension action interface
 */
export interface ISetCanvasDimensionAction extends INonUndoableAction, ICanvasDimension {
};

/**
 * Set pan mode
 */
export interface ISetCanvasPanAction extends INonUndoableAction {
    pan: boolean;
}

/**
 * Set format painter action interface
 */
export interface ISetFormatPainterAction extends INonUndoableAction {
    isFormatPainter: boolean;
}

/**
 *  Set snap option action interface
 */
export interface ISetSnapOptionsAction extends INonUndoableAction {
    snapOption: boolean;
};

export interface ISetCanvasModeAction extends INonUndoableAction {
    mode: CanvasMode;
}

/**
  * Enable editing action interface
  */
export interface ISetEditableAction extends INonUndoableAction {
        editing: boolean;
};

/**
  * Set operation in progress action interface
  */
export interface ISetOperationInProgressAction extends INonUndoableAction {
    progress: boolean;
};

/**
 * Set Froala License action interface
 */
export interface ISetFroalaLicenseAction extends INonUndoableAction {
 serialKey: string;
};

/**
 * Set selected curve point index
 */
export interface ISetIsCanvasTemplateAction extends INonUndoableAction {
    contextType?: string;
    isCanvasTemplate: boolean;
};

/**
 * Set selected curve point index
 */
export interface ISetSelectedCurvePointAction extends INonUndoableAction {
    pointIndex: number | undefined;
    preserveSelection?: boolean;
};

/**
 * Set canvas zoom value action interface
 */
export interface ISetCanvasZoomAction extends INonUndoableAction {
    zoomFactor: number;
};

/**
 * Set placeholder text format to "all", "label", or "value".
 */
export interface ISetPlaceholderTextFormatAction extends INonUndoableAction {
    placeholderTextFormat: string;
};

/**
 * Set canvas editor dimension
 * @param state Current state for canvas dimension reducer
 * @param action Reducer payload to set set canvas dimension
 * @param action.type Set dimension action type viz. 'SET_CANVAS_DIMENSION'
 * @param action.editorWidth The width to set
 * @param action.editorHeight The height to set
 * @returns New state for Canvas editor reducer
 */
export const setCanvasDimension  =
(state: ICanvasEditorState["canvasEditor"], action: ISetCanvasDimensionAction) => {
	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
	const { editorHeight, editorWidth } = action;
	newState.editorHeight = editorHeight  || newState.editorHeight;
	newState.editorWidth = editorWidth || newState.editorWidth;
	return newState;
};

/**
 * Set snap to grid on and off
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to set set snap options
 * @param action.type Set snap options action type viz. 'SET_SNAP_TO_GRID'
 * @param action.snapOption
 * @returns New state for Canvas editor reducer
 */
export const setSnapToGrid  =
(state: ICanvasEditorState["canvasEditor"], action: ISetSnapOptionsAction) => {
	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
	const { snapOption } = action;
	newState.isSnapToGrid = snapOption;
	return newState;
};

/**
 * Set snap to guide on and off
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to set set snap options
 * @param action.type Set snap options action type viz. 'SET_SNAP_TO_GUIDE'
 * @param action.snapOption
 * @returns New state for Canvas editor reducer
 */
export const setSnapToGuide  =
(state: ICanvasEditorState["canvasEditor"], action: ISetSnapOptionsAction) => {
	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
	const { snapOption } = action;
	newState.isSnapToGuide = snapOption;
	return newState;
};

/**
 * Set canvas mode appropriately.
 * @param state Current state of canvas editor reducer.
 * @param action Reducer payload to set canvas mode.
 */
export const setCanvasMode =
(state: ICanvasEditorState["canvasEditor"], action: ISetCanvasModeAction) => {
	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
	newState.mode = action.mode;
	return newState;
};

/**
 * Set format painter
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to set format painter
 * @param action.type Set format painter action type viz. 'SET_FORMAT_PAINTER'
 * @param action.isFormatPainter Format painter flag
 * @returns New state for Canvas editor reducer
 */
export const setFormatPainter =
    (state: ICanvasEditorState["canvasEditor"], action: ISetFormatPainterAction) => {
    	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
    	const { isFormatPainter } = action;
    	newState.isFormatPainterActive = isFormatPainter;
    	return newState;
    };

/**
 * Set edit to on and off
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to edit mode on/off
 * @param action.type Set edit mode action type viz. 'ENABLE_EDITING'
 * @param action.editing
 * @returns New state for Canvas editor reducer
 */
export const setEditable =
(state: ICanvasEditorState["canvasEditor"], action: ISetEditableAction) => {
	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
	newState.isEditEnabled = action.editing;
	return newState;
};

/**
 * Turn panning on or off
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to edit mode on/off
 * @returns New state for Canvas editor reducer
 */
export const setPanningMode =
(state: ICanvasEditorState["canvasEditor"], action: ISetCanvasPanAction) => {
	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
	newState.isPanning = action.pan;
	return newState;
};

/**
 * Set Froala license
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to set froala license
 * @param action.type Set froala license action type viz. 'SET_FROALA_LICENSE'
 * @param action.serialKey Froala serial key
 * @returns New state for Canvas editor reducer
 */
export const setFroalaLicense =
    (state: ICanvasEditorState["canvasEditor"], action: ISetFroalaLicenseAction) => {
    	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
    	newState.froalaLicense = action.serialKey;
    	return newState;
    };

/**
 * Set canvas container
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to set canvas container
 * @returns New state for Canvas editor reducer
 */
export const setCanvasContainer = (
	state: ICanvasEditorState["canvasEditor"],
	action: ISetCanvasContainerAction,
) => {
	const { canvasContainerEl } = action;
	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
	if (canvasContainerEl) {
		newState.canvasContainerEl = canvasContainerEl;
	}
	return newState;
};

/**
 * Set canvas zoom value
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to set canvas zoom
 * @returns New state for Canvas editor reducer
 */
export const setCanvasZoom =
(state: ICanvasEditorState["canvasEditor"], action: ISetCanvasZoomAction) => {
	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
	newState.zoomFactor = action.zoomFactor;
	return newState;
};

/**
 * Set isCanvasTemplate value
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to set isCanvasTemplate
 * @returns New state for Canvas editor reducer
 */
export const setIsCanvasTemplate = (
	state: ICanvasEditorState["canvasEditor"],
	action: ISetIsCanvasTemplateAction,
) => {
	const { contextType, isCanvasTemplate } = action;
	const newState = _.cloneDeep(state);
	newState.contextType = contextType;
	newState.isCanvasTemplate = isCanvasTemplate;
	return newState;
};

/**
 * Set selected curve point while editing curve.
 * Use cases for this API's are follows:-
 * 1. User can select and deselect the curve point (by clicking on point).
 *    In this case preserveSelection will be false and selectedCurvePoint is equal to pointIndex(number)
 *    then selectedCurvePoint to be undefined.
 * 2. While dragging the existing curve point if escape key is pressed then selected point should get deselected
 *    otherwise point should remain selected.
 *    a. If dragged point is not selected it will get selected using case 1.
 *    b. If point is already selected then for drag onPointerDown it get deselected so selecting same point in drag
 *       with preserveSelection true(kind of same case as 3.a ).
 *    c. If ESCAPE key is pressed during drag then preserveSelection is false and pointIndex is undefined
 *    d. On drag complete without escape then selected point will remain selected(in this case API is not called)
 * 3. While adding new point it follows same concept for escape key pressed as describe in case 2.
 *    a. Different scenario from case 2:-
 *       If 3rd curve point is selected and user added point in between point 2 and 3 so here
 *       selectedCurvePoint is already 3 in state and point index of new point is also 3 so if we do not have
 *       preserveSelection flag here then (newState.selectedCurvePoint !== pointIndex) will be false and
 *       selectedCurvePoint will be undefined however it should be 3 hence preserveSelection flag is introduced.
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to set text overflow
 */
export const setSelectedCurvePoint = (
	state: ICanvasEditorState["canvasEditor"],
	action: ISetSelectedCurvePointAction,
) => {
	const { pointIndex, preserveSelection } = action;
	const newState = _.cloneDeep(state);
	if ((newState.selectedCurvePoint === pointIndex) && !preserveSelection) {
		newState.selectedCurvePoint = undefined;
	} else {
		newState.selectedCurvePoint = pointIndex;
	}
	return newState;
};

/**
 * Set placeholder text format
 * @param state Current state for canvas editor reducer
 * @param action Reducer payload to set canvas container
 * @returns New state for Canvas editor reducer
 */
export const setPlaceholderTextFormat = (
	state: ICanvasEditorState["canvasEditor"],
	action: ISetPlaceholderTextFormatAction,
) => {
	const { placeholderTextFormat } = action;
	const newState: ICanvasEditorState["canvasEditor"] = { ...state };
	newState.placeholderTextFormat = placeholderTextFormat;
	return newState;
};